import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Shield, UserPlus, Link as LinkIcon, SlidersHorizontal, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import InviteUserDialog from "@/components/InviteUserDialog";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  director: "Director",
  dispatch_executive: "Dispatch",
  slitting_operator: "Accounts",
  press_operator: "Production",
  store_manager: "Store Manager",
  pending: "Pending",
};

const ROLE_BADGE = {
  director: "bg-slate-900 text-white",
  dispatch_executive: "bg-blue-100 text-blue-800 border border-blue-200",
  slitting_operator: "bg-orange-100 text-orange-800 border border-orange-200",
  press_operator: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  store_manager: "bg-teal-100 text-teal-800 border border-teal-200",
  pending: "bg-amber-100 text-amber-800 border border-amber-200",
};

const Users = () => {
  const { user: me } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [modulesFor, setModulesFor] = useState(null); // user whose modules we're editing
  const qc = useQueryClient();

  const buildInvite = (u) => {
    const fname = (u.name || u.email.split("@")[0]).split(" ")[0];
    return `Hi ${fname},

You've been added to the SR ELECTROSTEEL PRIVATE LIMITED ERP system as ${ROLE_LABELS[u.role] || u.role}.

To sign in:
1. Open ${window.location.origin}
2. Click "Continue with Google"
3. Use your company Gmail (${u.email})

You'll land in the dashboard immediately — no waiting for approval.

If you have any trouble accessing, ping me.`;
  };

  const copyInvite = async (u) => {
    const text = buildInvite(u);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("execCommand returned false");
      }
      toast.success(`Invite for ${u.email} copied — paste into Gmail`);
    } catch (err) {
      console.error("Copy failed", err);
      toast.error("Copy failed — try again or copy from the invite dialog");
    }
  };

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      return data;
    },
  });

  const setRoleMut = useMutation({
    mutationFn: async ({ user_id, role }) => {
      const { data } = await axios.patch(`${API}/users/${user_id}`, { role }, { withCredentials: true });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Updated ${data.name || data.email} to ${ROLE_LABELS[data.role] || data.role}`);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update role"),
  });

  const deleteMut = useMutation({
    mutationFn: async (user_id) => {
      await axios.delete(`${API}/users/${user_id}`, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success("User removed");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to remove user"),
  });

  const setModulesMut = useMutation({
    mutationFn: async ({ user_id, allowed_modules, perms }) => {
      // Save modules + master-perms in a single click. Both calls hit the
      // same user doc; order doesn't matter but we run them sequentially so
      // the second call's response is the authoritative serialised user.
      await axios.patch(
        `${API}/users/${user_id}/modules`,
        { allowed_modules },
        { withCredentials: true },
      );
      const { data } = await axios.patch(
        `${API}/users/${user_id}/master-perms`,
        perms || {},
        { withCredentials: true },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Modules updated for ${data.name || data.email}`);
      qc.invalidateQueries({ queryKey: ["users"] });
      setModulesFor(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update modules"),
  });

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="border-b border-slate-200 pb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Access control</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Shield className="h-6 w-6" /> ERP Users
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            New sign-ups land as <strong>Pending</strong>. Assign a role for defaults, or click <strong>Modules</strong> to pick exactly which pages a user can access.
          </p>
        </div>
        <Button
          data-testid="invite-user-btn"
          onClick={() => setInviteOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10"
        >
          <UserPlus className="h-4 w-4 mr-2" /> Invite user
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading users…</div>
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">User</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Email</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Current Role</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Change Role</TableHead>
                <TableHead className="text-right text-[10px] tracking-[0.2em] uppercase text-slate-500 w-12">·</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const isSelf = u.user_id === me?.user_id;
                return (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="avatar" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold">
                            {(u.name || u.email).slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">{u.name || "—"}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">(you)</span>}</p>
                          <p className="text-[11px] text-slate-500">{u.created_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono-data text-xs">{u.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm ${ROLE_BADGE[u.role] || ROLE_BADGE.pending}`} data-testid={`role-badge-${u.email}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={u.role}
                        onValueChange={(v) => setRoleMut.mutate({ user_id: u.user_id, role: v })}
                        disabled={isSelf}
                      >
                        <SelectTrigger data-testid={`role-select-${u.email}`} className="h-9 w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="director">Director</SelectItem>
                          <SelectItem value="dispatch_executive">Dispatch</SelectItem>
                          <SelectItem value="slitting_operator">Accounts</SelectItem>
                          <SelectItem value="press_operator">Production</SelectItem>
                          <SelectItem value="store_manager">Store Manager</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`modules-btn-${u.email}`}
                          onClick={() => setModulesFor(u)}
                          title={u.allowed_modules ? "Custom module access set" : "Set custom module access"}
                          className={`transition-colors p-1.5 ${u.allowed_modules ? "text-emerald-700 hover:text-emerald-900" : "text-slate-400 hover:text-slate-900"}`}
                          aria-label={`Modules for ${u.email}`}
                        >
                          <SlidersHorizontal className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`copy-invite-${u.email}`}
                          onClick={() => copyInvite(u)}
                          title="Copy invite for this user"
                          className="text-slate-400 hover:text-blue-700 transition-colors p-1.5"
                          aria-label={`Copy invite for ${u.email}`}
                        >
                          <LinkIcon className="h-4 w-4" />
                        </button>
                        <button
                          disabled={isSelf}
                          data-testid={`delete-user-${u.email}`}
                          onClick={() => setConfirmDelete(u)}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400 p-1.5"
                          aria-label="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              They will be signed out and lose access immediately. You can re-invite them later by asking them to sign in again — they will land back in the Pending queue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-user-btn"
              onClick={() => { deleteMut.mutate(confirmDelete.user_id); setConfirmDelete(null); }}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ModulesDialog
        target={modulesFor}
        onClose={() => setModulesFor(null)}
        onSave={(mods, perms) => setModulesMut.mutate({ user_id: modulesFor.user_id, allowed_modules: mods, perms })}
        onReset={() => setModulesMut.mutate({ user_id: modulesFor.user_id, allowed_modules: null, perms: {} })}
        saving={setModulesMut.isPending}
        selfUserId={me?.user_id}
      />

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
};

export default Users;

// ================== Modules Dialog ==================
function ModulesDialog({ target, onClose, onSave, onReset, saving, selfUserId }) {
  const [selected, setSelected] = useState(new Set());
  const [registry, setRegistry] = useState({ modules: [], role_defaults: {} });
  const [loading, setLoading] = useState(false);
  // Three per-user flags that let the user act on master entries WITHOUT
  // Director approval. Backend defaults each to false.
  const [perms, setPerms] = useState({
    can_create_masters_direct: false,
    can_edit_masters_direct: false,
    can_delete_masters_direct: false,
  });

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setLoading(true);
    // Seed the three perm flags from the user doc.
    setPerms({
      can_create_masters_direct: !!target.can_create_masters_direct,
      can_edit_masters_direct:   !!target.can_edit_masters_direct,
      can_delete_masters_direct: !!target.can_delete_masters_direct,
    });
    axios.get(`${API}/permissions/modules`, { withCredentials: true }).then(({ data }) => {
      if (cancelled) return;
      setRegistry(data);
      // Preselect current effective set: override if set, else role default
      const seed = target.allowed_modules
        ? new Set(target.allowed_modules)
        : new Set(data.role_defaults[target.role] || []);
      setSelected(seed);
    }).catch(() => toast.error("Failed to load module list"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [target]);

  if (!target) return null;

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const togglePerm = (key) => setPerms((prev) => ({ ...prev, [key]: !prev[key] }));

  // Group modules → top-level first, sub-modules nested under parent
  const topLevel = registry.modules.filter((m) => !m.parent);
  const childrenOf = (key) => registry.modules.filter((m) => m.parent === key);

  const isSelf = target.user_id === selfUserId;
  const isDirector = target.role === "director";

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modules-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5" /> Module access — {target.name || target.email}
          </DialogTitle>
          <DialogDescription>
            Tick modules this user can view and interact with. Sub-modules are nested. Unticking a module overrides the role default.
            {target.allowed_modules ? (
              <span className="ml-1 text-emerald-700 font-medium">Custom access is currently active.</span>
            ) : (
              <span className="ml-1 text-slate-500">Currently using role default ({ROLE_LABELS[target.role] || target.role}).</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {isDirector && (
          <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Directors always see every module (safety net). Changes to a director's module list are stored but not enforced.
          </div>
        )}

        {/* Per-user master-data permissions. Director-only, applies to every
            master type (RM Grade, SFG SKU, FG SKU, Supplier, Customer, Press,
            Employee). An unchecked box means the user still has to route the
            action through the Director's approval queue. */}
        <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="master-perms-section">
          <div>
            <p className="text-sm font-semibold text-slate-900">Master data — bypass director approval</p>
            <p className="text-[11px] text-slate-500">
              Tick the actions this user can perform on master entries (all types) without needing Director sign-off.
              Leave unticked to keep the current approval workflow.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="flex items-start gap-2 cursor-pointer bg-white border border-slate-200 rounded-sm p-2" data-testid="perm-row-create">
              <Checkbox
                checked={perms.can_create_masters_direct}
                onCheckedChange={() => togglePerm("can_create_masters_direct")}
                data-testid="perm-cb-can_create_masters_direct"
                disabled={isDirector}
                className="mt-0.5"
              />
              <span className="text-xs text-slate-800">
                <span className="font-semibold text-slate-900 block">Can add without director approval?</span>
                New entries go <b>active</b> immediately.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer bg-white border border-slate-200 rounded-sm p-2" data-testid="perm-row-edit">
              <Checkbox
                checked={perms.can_edit_masters_direct}
                onCheckedChange={() => togglePerm("can_edit_masters_direct")}
                data-testid="perm-cb-can_edit_masters_direct"
                disabled={isDirector}
                className="mt-0.5"
              />
              <span className="text-xs text-slate-800">
                <span className="font-semibold text-slate-900 block">Can edit existing without director approval?</span>
                Changes apply instantly to any master.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer bg-white border border-slate-200 rounded-sm p-2" data-testid="perm-row-delete">
              <Checkbox
                checked={perms.can_delete_masters_direct}
                onCheckedChange={() => togglePerm("can_delete_masters_direct")}
                data-testid="perm-cb-can_delete_masters_direct"
                disabled={isDirector}
                className="mt-0.5"
              />
              <span className="text-xs text-slate-800">
                <span className="font-semibold text-slate-900 block">Can delete without director approval?</span>
                Skips the delete-request queue for all masters.
              </span>
            </label>
          </div>
          {isDirector && (
            <p className="text-[11px] text-amber-700">Directors already bypass every master-data check — these toggles have no effect on a director account.</p>
          )}
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading modules…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 py-2">
            {topLevel.map((m) => {
              const kids = childrenOf(m.key);
              return (
                <div key={m.key} className="border border-slate-200 rounded-sm p-3 bg-white">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`module-row-${m.key}`}>
                    <Checkbox
                      checked={selected.has(m.key)}
                      onCheckedChange={() => toggle(m.key)}
                      data-testid={`module-cb-${m.key}`}
                    />
                    <span className="text-sm font-semibold text-slate-900">{m.label}</span>
                  </label>
                  {kids.length > 0 && (
                    <div className="mt-2 pl-6 space-y-1.5">
                      {kids.map((c) => (
                        <label key={c.key} className="flex items-center gap-2 cursor-pointer" data-testid={`module-row-${c.key}`}>
                          <Checkbox
                            checked={selected.has(c.key)}
                            onCheckedChange={() => toggle(c.key)}
                            data-testid={`module-cb-${c.key}`}
                          />
                          <span className="text-xs text-slate-700">{c.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onReset}
            disabled={saving || !target.allowed_modules || (isSelf && !selected.has("erp_users"))}
            data-testid="modules-reset-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reset to role default
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving} data-testid="modules-cancel-btn">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => onSave(Array.from(selected), perms)}
              disabled={saving || (isSelf && !selected.has("erp_users"))}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="modules-save-btn"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
